import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSocket } from "../../../../context/SocketContext";
import {
  useChats,
  useChatMessages,
  useSendMessage,
  useDeleteMessage,
} from "../../../../api/Chats/chats";
import { useEditMessage } from "../../../../api/Chats/chats";
import { useQueryClient } from "@tanstack/react-query";

import ChatHeader from "./ChatHeader";
import MessagesList from "./MessagesList";
import MessageInput from "./MessageInput";
import ChatInfo from "./ChatInfo";
import EditMessageModal from "./MessagesList/EditMessageModal";

import { useDropzone } from "react-dropzone";

export default function Message() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const chatId = params.get("chatId");

  // ✅ Extract userId first but DON'T return early yet
  let userId = "";
  let authError = false;
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      userId = decoded.userid || decoded._id || decoded.id || decoded.sub;
    }
    if (!userId) {
      authError = true;
    }
  } catch (error) {
    console.error("Failed to decode user token:", error);
    authError = true;
  }

  // ✅ ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const { data: chatPages, isLoading: chatLoading } = useChats({
    token: localStorage.token,
    limit: 10,
    search: "",
  });

  const queryClient = useQueryClient();

  // ✅ FIX: Get current chat from ALL cached queries, not just the default one
  const getCurrentChat = useCallback(() => {
    if (!chatId) return null;

    // First, try to find in the default query
    const chats = chatPages?.pages?.flatMap((p) => p.chats) ?? [];
    let foundChat = chats.find((c) => c._id === chatId);
    
    if (foundChat) return foundChat;

    // If not found, search in ALL cached chat queries (including search queries)
    const allChatQueries = queryClient.getQueriesData({ queryKey: ["chats"] });
    
    for (const [queryKey, data] of allChatQueries) {
      if (data?.pages) {
        const allChats = data.pages.flatMap((p) => p.chats || []);
        foundChat = allChats.find((c) => c._id === chatId);
        if (foundChat) {
          console.log("Found chat in cached query:", queryKey);
          return foundChat;
        }
      }
    }

    return null;
  }, [chatId, chatPages, queryClient]);

  const currentChat = getCurrentChat();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: msgLoading,
  } = useChatMessages(chatId, { enabled: !!chatId });

  const msgs =
    data?.pages
      .flatMap((page) => page.messages)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) || [];

  const { mutate: sendMessage, isPending: sending } = useSendMessage();
  const { mutate: deleteMessage } = useDeleteMessage();
  const { mutate: editMessage, isPending: editing } = useEditMessage();

  const [input, setInput] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pastedImages, setPastedImages] = useState([]);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  const messageListRef = useRef();
  const inputRef = useRef();
  const typingTimeoutRef = useRef(null);
  const lastSeenMessageRef = useRef(null);

  const {
    socket,
    connectionStatus,
    isUserOnline,
    joinChat,
    leaveChat,
    sendTyping,
    stopTyping,
    clearChatNotifications,
    getChatUnreadCount,
  } = useSocket() || {};

  const otherUser =
    !currentChat?.isGroupChat && currentChat?.users
      ? currentChat.users.find((u) => {
          const uId = u?._id || u?.id;
          return uId && uId !== userId;
        })
      : null;
  const isOtherUserOnline = otherUser ? isUserOnline(otherUser._id) : false;
  const currentUnreadCount = getChatUnreadCount
    ? getChatUnreadCount(chatId)
    : 0;

  const canSendMessages = currentChat?.isGroupChat
    ? true
    : otherUser?.isActive !== false;

  // Handle edit message
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setShowEditModal(true);
  };

  const handleSaveEdit = (messageId, newContent) => {
    editMessage(
      { messageId, content: newContent },
      {
        onSuccess: () => {
          setShowEditModal(false);
          setEditingMessage(null);
        },
      },
    );
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingMessage(null);
  };

  const removePastedImage = (index) => {
    setPastedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (filesOrFile = null) => {
    const hasContent = input.trim();

    let allFiles = [];
    if (filesOrFile) {
      allFiles = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    }

    const hasFiles = allFiles.length > 0;

    if (!hasContent && !hasFiles) return;
    if (!chatId || sending) return;

    const messageContent = input.trim();
    setInput("");
    const currentReply = replyingTo;
    setReplyingTo(null);
    setPastedImages([]);

    if (inputRef.current) inputRef.current.style.height = "40px";

    stopTyping(chatId);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    try {
      const messageData = {
        chatId,
        content: messageContent,
        files: allFiles.length > 0 ? allFiles : null,
        ...(currentReply && { replyTo: currentReply._id }),
      };

      await sendMessage(messageData, {
        onSuccess: (newMessage) => {
          clearChatNotifications(chatId);
          if (socket) {
            socket.emit("messageSent", {
              chatId,
              messageId: newMessage._id || newMessage.id,
              _id: newMessage._id || newMessage.id,
              content: messageContent,
              senderId: userId,
              files: newMessage.files || [],
            });
          }
          if (messageListRef.current) {
            setTimeout(() => {
              messageListRef.current.scrollTo({
                top: messageListRef.current.scrollHeight,
                behavior: "smooth",
              });
            }, 100);
          }
        },
        onError: (error) => {
          console.error("Error sending message:", error);
          setInput(messageContent);
          if (currentReply) setReplyingTo(currentReply);
          setPastedImages(allFiles);
        },
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setInput(messageContent);
      if (currentReply) setReplyingTo(currentReply);
      setPastedImages(allFiles);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      setIsDragging(false);
      if (acceptedFiles.length > 0) {
        handleSendMessage(acceptedFiles);
      }
    },
    [chatId, userId, input, replyingTo, sending],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    noClick: true,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  // Handle paste images
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!chatId) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.indexOf("image") !== -1) {
          e.preventDefault();

          const blob = item.getAsFile();
          if (blob) {
            const timestamp = Date.now();
            const fileName = `pasted-image-${timestamp}.png`;
            const file = new File([blob], fileName, { type: blob.type });
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        setPastedImages((prev) => [...prev, ...imageFiles]);

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [chatId]);

  useEffect(() => {
    setIsDragging(isDragActive);
  }, [isDragActive]);

  // Escape key navigates back to the chat list
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        navigate("/chats");
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate]);

  // Handle chat change loading state
  useEffect(() => {
    if (chatId) {
      setIsLoadingChat(true);
      const timer = setTimeout(() => {
        setIsLoadingChat(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [chatId]);

  // Reset state on chat change
  useEffect(() => {
    setIsTyping(false);
    setTypingUsers(new Set());
    setInput("");
    setShowEmoji(false);
    setShowChatInfo(false);
    setReplyingTo(null);
    setInitialScrollDone(false);
    setShowScrollButton(false);
    setPastedImages([]);
    setEditingMessage(null);
    setShowEditModal(false);
    lastSeenMessageRef.current = null;
  }, [chatId]);

  useEffect(() => {
    if (msgs.length > 0 && !initialScrollDone) {
      setTimeout(() => {
        messageListRef.current?.scrollTo({
          top: messageListRef.current.scrollHeight,
          behavior: "auto",
        });
        setInitialScrollDone(true);
      }, 100);
    }
  }, [msgs.length, initialScrollDone]);

  // Typing timeout
  useEffect(() => {
    if (!isTyping) return;
    const timeout = setTimeout(() => {
      setIsTyping(false);
      setTypingUsers(new Set());
    }, 5000);
    return () => clearTimeout(timeout);
  }, [isTyping]);

  useEffect(() => {
    if (!chatId || !socket || !isAtBottom || msgs.length === 0) return;

    const lastMessage = msgs[msgs.length - 1];

    if (
      lastMessage &&
      lastMessage.sender._id !== userId &&
      lastSeenMessageRef.current !== lastMessage._id
    ) {
      const alreadySeen = lastMessage.readBy?.some((readUser) => {
        const readUserId =
          typeof readUser === "string" ? readUser : readUser._id;
        return readUserId === userId;
      });

      if (!alreadySeen) {
        const timeoutId = setTimeout(() => {
          socket.emit("markMessageSeen", {
            chatId,
            messageId: lastMessage._id,
            userId,
          });
          clearChatNotifications(chatId);
          lastSeenMessageRef.current = lastMessage._id;
        }, 500);

        return () => clearTimeout(timeoutId);
      } else {
        lastSeenMessageRef.current = lastMessage._id;
      }
    }
  }, [chatId, socket, isAtBottom, msgs, userId]);

  // Socket event handling
  useEffect(() => {
    if (!socket || !chatId) return;

    joinChat(chatId);

    const handleNewMessage = (message) => {
      if (message.chat === chatId || message.chatId === chatId) {
        queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
          if (!oldData)
            return { pages: [{ messages: [message] }], pageParams: [1] };

          const { pages, pageParams } = oldData;
          const allMessages = pages.flatMap((p) => p.messages || []);
          if (allMessages.find((m) => m._id === message._id)) return oldData;

          const newPages = [...pages];
          const lastPage = newPages[newPages.length - 1];
          newPages[newPages.length - 1] = {
            ...lastPage,
            messages: [...lastPage.messages, message],
          };
          return { pages: newPages, pageParams };
        });

        if (isAtBottom) {
          setTimeout(() => {
            messageListRef.current?.scrollTo({
              top: messageListRef.current.scrollHeight,
              behavior: "smooth",
            });
          }, 100);
        }
      }
    };

    const handleMessageEdited = ({ messageId, content, edited, editedAt }) => {
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) return oldData;

        const { pages, pageParams } = oldData;

        const newPages = pages.map((page) => ({
          ...page,
          messages: page.messages.map((msg) => {
            if (msg._id === messageId) {
              return {
                ...msg,
                content,
                edited,
                editedAt,
              };
            }
            return msg;
          }),
        }));

        return { pages: newPages, pageParams };
      });

      console.log("✅ Message edited via socket:", messageId);
    };

    const handleUserTyping = ({ chatId: cId, userId: uId, username }) => {
      if (cId === chatId && uId !== userId) {
        setTypingUsers((prev) => new Set([...prev, { userId: uId, username }]));
        setIsTyping(true);
      }
    };

    const handleUserStoppedTyping = ({ chatId: cId, userId: uId }) => {
      if (cId === chatId && uId !== userId) {
        setTypingUsers((prev) => {
          const newSet = new Set(
            [...prev].filter((user) => user.userId !== uId),
          );
          if (newSet.size === 0) setIsTyping(false);
          return newSet;
        });
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("messageEdited", handleMessageEdited);
    socket.on("userTyping", handleUserTyping);
    socket.on("userStoppedTyping", handleUserStoppedTyping);

    return () => {
      leaveChat(chatId);
      stopTyping(chatId);
      socket.off("newMessage", handleNewMessage);
      socket.off("messageEdited", handleMessageEdited);
      socket.off("userTyping", handleUserTyping);
      socket.off("userStoppedTyping", handleUserStoppedTyping);
      setIsTyping(false);
      setTypingUsers(new Set());
    };
  }, [socket, chatId, isAtBottom, queryClient, userId]);

  // Reply handlers
  const handleReply = (message) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  };
  const handleCancelReply = () => setReplyingTo(null);

  const scrollToMessage = (messageId) => {
    setHighlightedMessageId(messageId);

    const messageElement = document.querySelector(
      `[data-message-id="${messageId}"]`,
    );
    if (messageElement && messageListRef.current) {
      const container = messageListRef.current;
      const messageTop = messageElement.offsetTop;
      const containerHeight = container.clientHeight;
      const messageHeight = messageElement.clientHeight;

      container.scrollTo({
        top: messageTop - containerHeight / 2 + messageHeight / 2,
        behavior: "smooth",
      });

      messageElement.classList.add(
        "bg-blue-50",
        "transition-all",
        "duration-500",
      );
      setTimeout(() => {
        messageElement.classList.remove("bg-blue-50");
        setHighlightedMessageId(null);
      }, 2000);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (!socket || !chatId) return;

    if (e.target.value.trim()) {
      sendTyping(chatId);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => stopTyping(chatId), 3000);
    } else {
      stopTyping(chatId);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleCopy = (text) => navigator.clipboard.writeText(text || "");
  const handleDelete = (id) => deleteMessage(id);

  const handleScroll = () => {
    const el = messageListRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    if (scrollTop < 200 && hasNextPage && !isFetchingNextPage) {
      const previousScrollHeight = scrollHeight;

      fetchNextPage().then(() => {
        requestAnimationFrame(() => {
          if (messageListRef.current) {
            const newScrollHeight = messageListRef.current.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeight;
            messageListRef.current.scrollTop = scrollTop + heightDifference;
          }
        });
      });
    }

    const isAtBottomNow = scrollHeight - scrollTop - clientHeight < 50;
    setIsAtBottom(isAtBottomNow);
    setShowScrollButton(!isAtBottomNow);
  };

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo({
        top: messageListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // ✅ NOW WE CAN DO CONDITIONAL RETURNS (AFTER ALL HOOKS)

  // Check for auth error
  if (authError) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-red-500">
        Authentication error. Please log in again.
      </div>
    );
  }

  // No chat selected
  if (!chatId) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-gray-500">
        <div className="text-center">
          <div className="text-lg mb-2">💬</div>
          <div>Select a chat to start messaging</div>
          <div className="text-sm text-gray-400 mt-1">
            Connection: {connectionStatus}
          </div>
        </div>
      </div>
    );
  }

  // Show loading when initially loading chats list OR when loading messages OR when switching chats
  if (chatLoading || msgLoading || (chatId && !currentChat && isLoadingChat)) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-gray-500">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-2"></div>
          <div>Loading messages...</div>
        </div>
      </div>
    );
  }

  // Chat not found (only show after loading is complete)
  if (!currentChat && !chatLoading && !isLoadingChat) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-red-500">
        Chat not found.
      </div>
    );
  }

  // For non-group chats, show a loading state if users haven't loaded yet
  if (currentChat && !currentChat.isGroupChat && !currentChat.users?.length) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-gray-500">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-2"></div>
          <div>Loading chat details...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col flex-1 h-full relative ${
        isDragging ? "bg-blue-50 border-4 border-dashed border-blue-400" : ""
      }`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-blue-50 bg-opacity-90 pointer-events-none">
          <div className="text-center">
            <div className="text-6xl mb-4">📁</div>
            <div className="text-2xl font-bold text-blue-600">
              Drop files here
            </div>
            <div className="text-sm text-gray-600 mt-2">
              Release to send files
            </div>
          </div>
        </div>
      )}

      <ChatHeader
        currentChat={currentChat}
        otherUser={otherUser}
        userId={userId}
        isOtherUserOnline={isOtherUserOnline}
        connectionStatus={connectionStatus}
        currentUnreadCount={currentUnreadCount}
        onHeaderClick={() => setShowChatInfo(true)}
        messages={msgs}
        onSearchResultClick={scrollToMessage}
      />

      <MessagesList
        ref={messageListRef}
        msgs={msgs}
        userId={userId}
        isTyping={isTyping}
        typingUsers={typingUsers}
        onScroll={handleScroll}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onReply={handleReply}
        onEdit={handleEditMessage}
        chat={currentChat}
        isFetchingNextPage={isFetchingNextPage}
      />

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-24 right-8 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all duration-300 z-10 border border-gray-200"
          aria-label="Scroll to bottom"
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      )}

      {canSendMessages ? (
        <div className="border-t bg-white">
          <MessageInput
            input={input}
            inputRef={inputRef}
            sending={sending}
            connectionStatus={connectionStatus}
            showEmoji={showEmoji}
            replyingTo={replyingTo}
            members={currentChat?.users || []}
            onInputChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            onSend={handleSendMessage}
            onEmojiToggle={() => setShowEmoji((v) => !v)}
            onSetInput={setInput}
            onCancelReply={handleCancelReply}
            otherUser={otherUser}
            pastedImages={pastedImages}
            onRemovePastedImage={removePastedImage}
          />
        </div>
      ) : (
        <div className="border-t bg-red-50">
          <div className="px-3 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-red-400">
              <div>
                <div className="font-medium text-sm">Cannot Send Messages</div>
                <div className="text-sm text-red-500">
                  This user's account is inactive. Messaging is not available.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showChatInfo && (
        <ChatInfo
          chatId={chatId}
          currentChat={currentChat}
          currentUserId={userId}
          otherUser={otherUser}
          isOtherUserOnline={isOtherUserOnline}
          onClose={() => setShowChatInfo(false)}
        />
      )}

      {showEditModal && editingMessage && (
        <EditMessageModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          message={editingMessage}
          onSave={handleSaveEdit}
          isSaving={editing}
        />
      )}
    </div>
  );
}